import { Box, HStack, VStack, Text, Icon } from '@chakra-ui/react';
import {
  MdElectricalServices as ConnectIcon,
  MdCloudDownload as ImportIcon,
  MdFolder as CollectionIcon,
  MdSearch as SearchIcon,
  MdLightMode as CookModeIcon,
  MdGroup as HouseholdIcon,
} from 'react-icons/md';
const LOGO_SRC = '/favicon.png';

interface FeatureItem {
  icon: React.ElementType;
  title: string;
  desc: string;
  prompt: string;
}

const FEATURES: FeatureItem[] = [
  {
    icon: ConnectIcon,
    title: 'Connect Claude',
    desc: 'Give Claude access to your recipes',
    prompt: 'How do I connect Claude to my Byte Pantry recipes?',
  },
  {
    icon: ImportIcon,
    title: 'Import recipes',
    desc: 'URL, photo, dictate, manually, from a shared link or directly from Instagram & TikTok',
    prompt: 'How do I import recipes?',
  },
  {
    icon: CollectionIcon,
    title: 'Collections & shopping lists',
    desc: 'Group recipes and get a combined shopping list',
    prompt: 'How do collections and shopping lists work?',
  },
  {
    icon: SearchIcon,
    title: 'Search & filters',
    desc: 'Find recipes by ingredient, tag, or rating',
    prompt: 'How do search and filters work?',
  },
  {
    icon: CookModeIcon,
    title: 'Cook mode',
    desc: 'Keep your screen awake while you cook',
    prompt: 'How does cook mode work?',
  },
  {
    icon: HouseholdIcon,
    title: 'Sharing & households',
    desc: 'Share recipes or cook together as a household',
    prompt: 'How do I share a recipe or invite someone to my household?',
  },
];

interface ChatHelpScreenProps {
  onSelectPrompt: (prompt: string) => void;
}

export default function ChatHelpScreen({ onSelectPrompt }: ChatHelpScreenProps) {
  return (
    <VStack gap={3} align="stretch" py={3} px={1}>
      {/* Hero — compact horizontal layout matching ChatWelcomeCard */}
      <Box
        bg="bg.surface"
        border="1.5px solid"
        borderColor="accent.secondaryBorder"
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="0 2px 16px rgba(0,0,0,0.08)"
      >
        <HStack gap={4} px={5} py={4} align="center">
          <Box w="44px" h="44px" flexShrink={0}>
            <img src={LOGO_SRC} alt="Chef Assistant" width={44} height={44} style={{ display: 'block', objectFit: 'contain' }} />
          </Box>
          <VStack gap={0} align="start" flex={1}>
            <Text fontFamily="heading" fontWeight="800" fontSize="md" color="text.primary">
              How can I help?
            </Text>
            <Text fontFamily="body" fontWeight="300" fontSize="sm" color="text.muted" lineHeight="1.5">
              Tap a feature — or just ask me anything.
            </Text>
          </VStack>
        </HStack>
      </Box>

      {/* Section label */}
      <Text
        fontSize="10px"
        fontWeight="600"
        color="text.muted"
        textTransform="uppercase"
        letterSpacing="0.1em"
        px={1}
      >
        What Byte Pantry can do
      </Text>

      {/* Feature cards */}
      {FEATURES.map((f) => (
        <Box
          key={f.title}
          as="button"
          onClick={() => onSelectPrompt(f.prompt)}
          bg="bg.surface"
          border="1.5px solid"
          borderColor="border.muted"
          borderRadius="xl"
          p={3}
          display="flex"
          alignItems="center"
          gap={3}
          w="100%"
          textAlign="left"
          cursor="pointer"
          _hover={{ borderColor: 'accent.secondary', bg: 'accent.secondarySubtle' }}
          transition="all 0.12s"
        >
          <Box
            w="34px"
            h="34px"
            borderRadius="9px"
            bg="bg.surface"
            border="1px solid"
            borderColor="border.muted"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Icon as={f.icon} boxSize={4} color="text.muted" />
          </Box>
          <Box flex={1} minW={0}>
            <Text fontFamily="body" fontWeight="500" fontSize="13px" color="text.primary" mb="1px">
              {f.title}
            </Text>
            <Text fontFamily="body" fontWeight="300" fontSize="11px" color="text.muted" lineHeight="1.4">
              {f.desc}
            </Text>
          </Box>
          <Text color="text.muted" fontSize="md" flexShrink={0}>›</Text>
        </Box>
      ))}
    </VStack>
  );
}
